import React, { useState, useEffect } from 'react';
import type { FC } from 'react';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { getPages,createPage,updatePage,deletePage } from '../services/pageService';
import { Link } from 'react-router-dom';
import { number } from 'yup';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface Page {
  page_id?: number;
  page_title: string;
  page_description: string;
  page_slug: string;
  page_status: 'Active' | 'Inactive';
}


type FormData = Omit<Page, 'id'>;

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const Pages: FC = () => {
   const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });
   useEffect(() => {
      fetchPages();
    }, []);

  // const [services, setServices] = useState<Array<{ service_id: number; name: string }>>([]);

  const [formData, setFormData] = useState<FormData>({
    page_title: '',
    page_description: '',
    page_slug: '',
    page_status: 'Active'
  });

  const fetchPages = async () => {
      try {
        setLoading(true);
        setError(''); // Clear any previous errors
        const response = await getPages();
        
        console.log('🔍 Full API response:', response);
        console.log('🔍 Response type:', typeof response);
        console.log('🔍 Response keys:', Object.keys(response || {}));
        
        // Try multiple possible data locations
        let pageData = null;
        
        if (response?.data?.data && Array.isArray(response.data.data)) {
          pageData = response.data.data;
          console.log('🔍 Using response.data.data (nested)');
        } else if (response?.data && Array.isArray(response.data)) {
          pageData = response.data;
          console.log('🔍 Using response.data (direct array)');
        } else if (Array.isArray(response)) {
          pageData = response;
          console.log('🔍 Using response (direct array)');
        } else if (response?.data?.pages && Array.isArray(response.data.pages)) {
          pageData = response.data.pages;
          console.log('🔍 Using response.data.pages');
        } else if (response?.pages && Array.isArray(response.pages)) {
          pageData = response.pages;
          console.log('🔍 Using response.pages');
        } else {
          console.log('🔍 Could not find Pages array in response');
          console.log('🔍 Available data:', response);
          pageData = [];
        }
        
        console.log('🔍 Final pages data:', pageData);
        console.log('🔍 Pages data length:', pageData?.length || 0);
        
        setPages(pageData || []);
      } catch (err) {
        setError('Failed to fetch pages. Please try again.');
        console.error('❌ Error fetching pages:', err);
      } finally {
        setLoading(false);
      }
    };

    
  useEffect(() => {
    fetchPages();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSelectChange = (e: SelectChangeEvent<string | number>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
    setLoading(true);

    const basePageData = {
      page_title: formData.page_title,
      page_description: formData.page_description,
      page_slug: formData.page_slug,
      page_status: formData.page_status
    };

    // ✅ Update
    if (editingPage) {
      if (!editingPage.page_id) {
        showSnackbar("Invalid page id", "error");
        return;
      }

      await updatePage(editingPage.page_id, basePageData);
      showSnackbar("Page updated successfully", "success");
    } else {
      // ✅ Create
      await createPage(basePageData);
      showSnackbar("Page created successfully", "success");
    }

    fetchPages();
    setOpen(false);

    // reset
    setFormData({
      page_title: "",
      page_description: "",
      page_slug: "",
      page_status: "Active"
    });

  } catch (error) {
    console.error("Error saving Page:", error);
    showSnackbar("Failed to save Page", "error");
  } finally {
    setLoading(false);
  }
};


  const handleClose = () => {
    setOpen(false);
    setEditingPage(null);
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleDelete = async (page_id: number) => {
      if (window.confirm('Are you sure you want to delete this Page?')) {
        try {
          setLoading(true);
          await deletePage(page_id, {page_status:'Delete'});
          showSnackbar('Page deleted successfully', 'success');
  
          // Refresh the banners list
          const response = await getPages();
          const data = Array.isArray(response) ? response : [];
          setPages(data);
          fetchPages();
        } catch (err) {
          console.error('Error deleting Page:', err);
          showSnackbar('Failed to delete Page', 'error');
        } finally {
          setLoading(false);
        }
      }
    };

  

  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        
        
        <Typography variant="h4"  fontWeight="bold">Pages Management</Typography>
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          Add Page
        </Button>
      </Box>

      {/* Dialog */}
      
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingPage ? 'Edit Page' : 'Add New Page'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr' }}>
              <TextField
                name="page_title"
                label="Title"
                value={formData.page_title}
                onChange={handleChange}
                fullWidth
                margin="normal"
                sx={{ gridColumn: '1 / -1' }}
              />

               <Box sx={{ gridColumn: "1 / -1", mt: 2 }}>
                <Typography sx={{ mb: 1 }}>Description</Typography>
                <ReactQuill
                  value={formData.page_description}
                  onChange={(value) =>
                    setFormData((prev) => ({
                      ...prev,
                      page_description: value,
                    }))
                  }
                  theme="snow"
                />
              </Box>
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="page_status"
                  value={formData.page_status}
                  onChange={handleSelectChange}
                  required
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
              
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained">
              {editingPage ? 'Update' : 'Create'} Page
            </Button>
          </DialogActions>
        </form>
      </Dialog>

     

        <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 1 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Page Slug</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>

              {pages.length === 0 && !loading ? (
                              <TableRow>
                                <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                  <Typography variant="body1" color="text.secondary">
                                    No Pages found. Click "Add New Page" to create one.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
              pages.map((page) => (
                <TableRow key={page.page_id} hover>
                  <TableCell>{page.page_title}</TableCell>
                  
                 <TableCell>
                    <div
                      dangerouslySetInnerHTML={{ __html: page.page_description }}
                    />
                  </TableCell>
                  <TableCell>{page.page_slug}</TableCell>
                  <TableCell>
                    <Chip 
                      label={page.page_status} 
                      color={page.page_status === 'Active' ? 'success' : 'default'} 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    
                      <IconButton onClick={() => {
                        setEditingPage(page);
                        setFormData(page);
                        setOpen(true);
                      }}>
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton 
                        onClick={() => handleDelete(page.page_id!)} 
                        color="error" 
                        size="small"
                        disabled={loading}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    
                  </TableCell>
                </TableRow>
               ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      
      <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <Alert 
                onClose={handleSnackbarClose}
                severity={snackbar.severity}
                sx={{ width: '100%' }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
    </Box>
  );
};

export default Pages;
